import { useState, useEffect } from 'react';
import { Plus, Trash2, Download, Check, Crown, ChevronRight, ChevronLeft, Network, AlertCircle } from 'lucide-react';
import useStore from '../lib/store';
import { getAllTopics } from '../data/ocrTopics';

const DIFFICULTY_OPTIONS = ['Beginner', 'Intermediate', 'Advanced'];

export default function LessonBuilder() {
  const [step, setStep] = useState(1);
  const devices = useStore(state => state.devices);
  const connections = useStore(state => state.connections);
  
  const [lessonData, setLessonData] = useState({
    title: '',
    difficulty: 'Beginner',
    duration: '15 minutes',
    curriculum: 'KS3 Computing - Networks',
    description: '',
    scenario: '', // Optional scenario/context
    ocrTopics: [],
    objectives: [''],
    challenges: [],
  });

  const allTopics = getAllTopics();

  // Check if network is empty
  const hasNetwork = devices.length > 0;

  const updateField = (field, value) => {
    setLessonData(prev => ({ ...prev, [field]: value }));
  };

  const addObjective = () => {
    setLessonData(prev => ({
      ...prev,
      objectives: [...prev.objectives, ''],
    }));
  };

  const updateObjective = (index, value) => {
    setLessonData(prev => ({
      ...prev,
      objectives: prev.objectives.map((obj, i) => i === index ? value : obj),
    }));
  };

  const removeObjective = (index) => {
    setLessonData(prev => ({
      ...prev,
      objectives: prev.objectives.filter((_, i) => i !== index),
    }));
  };

  const toggleTopic = (topicCode) => {
    setLessonData(prev => ({
      ...prev,
      ocrTopics: prev.ocrTopics.includes(topicCode)
        ? prev.ocrTopics.filter(code => code !== topicCode)
        : [...prev.ocrTopics, topicCode],
    }));
  };

  const addChallenge = () => {
    setLessonData(prev => ({
      ...prev,
      challenges: [
        ...prev.challenges,
        {
          id: prev.challenges.length + 1,
          title: '',
          description: '',
          hint: '',
          solution: '',
        },
      ],
    }));
  };

  const updateChallenge = (index, field, value) => {
    setLessonData(prev => ({
      ...prev,
      challenges: prev.challenges.map((ch, i) =>
        i === index ? { ...ch, [field]: value } : ch
      ),
    }));
  };

  const removeChallenge = (index) => {
    setLessonData(prev => ({
      ...prev,
      challenges: prev.challenges.filter((_, i) => i !== index).map((ch, i) => ({
        ...ch,
        id: i + 1,
      })),
    }));
  };

  const generateLessonCode = () => {
    const id = 'lesson-' + lessonData.title.toLowerCase().replace(/[^a-z0-9]+/g, '-');
    
    // Convert current network devices to lesson format
    const lessonDevices = devices.map(device => {
      const deviceObj = {
        name: device.name,
        type: device.type,
        x: device.x,
        y: device.y,
        powered: device.powered,
        ip: device.ip,
        subnet: device.subnet,
        gateway: device.gateway,
        networkTag: device.networkTag || '',
      };

      // Add router interfaces if applicable
      if (device.type === 'router' && device.interfaces) {
        deviceObj.interfaces = device.interfaces;
      }

      return deviceObj;
    });

    // Convert connections to lesson format
    const lessonConnections = connections.map(conn => {
      const device1 = devices.find(d => d.id === conn.device1);
      const device2 = devices.find(d => d.id === conn.device2);
      
      return {
        from: device1?.name || 'Unknown',
        to: device2?.name || 'Unknown',
        fromPort: conn.port1,
        toPort: conn.port2,
      };
    });

    // Create lesson object
    const lesson = {
      id: id,
      title: lessonData.title,
      difficulty: lessonData.difficulty,
      duration: lessonData.duration,
      curriculum: lessonData.curriculum,
      description: lessonData.description,
      ocrTopics: lessonData.ocrTopics,
      objectives: lessonData.objectives.filter(o => o.trim()),
      topology: {
        devices: lessonDevices,
        connections: lessonConnections,
      },
      challenges: lessonData.challenges,
    };
    
    // Add scenario only if provided
    if (lessonData.scenario && lessonData.scenario.trim()) {
      lesson.scenario = lessonData.scenario.trim();
    }

    // Convert to pretty JSON
    return JSON.stringify(lesson, null, 2);
  };

  const downloadLesson = () => {
    const code = generateLessonCode();
    const blob = new Blob([code], { type: 'application/octet-stream' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const filename = lessonData.title ? 
      `${lessonData.title.toLowerCase().replace(/\s+/g, '-')}.lesson` : 
      'untitled-lesson.lesson';
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <div className="builder-step">
            <h3 className="step-title">📋 Lesson Information</h3>
            <p className="step-description">Add details about your lesson</p>
            
            {/* Network Preview */}
            <div className="network-preview">
              <div className="preview-header">
                <Network size={18} />
                <span>Current Network</span>
              </div>
              <div className="preview-stats">
                <div className="stat">
                  <strong>{devices.length}</strong>
                  <span>Devices</span>
                </div>
                <div className="stat">
                  <strong>{connections.length}</strong>
                  <span>Connections</span>
                </div>
              </div>
              <div className="preview-note">
                💡 This network will be included in your lesson
              </div>
            </div>
            
            <div className="form-group">
              <label>Lesson Title *</label>
              <input
                type="text"
                className="form-input"
                value={lessonData.title}
                onChange={(e) => updateField('title', e.target.value)}
                placeholder="e.g., Introduction to Network Routing"
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Difficulty Level *</label>
                <select
                  className="form-select"
                  value={lessonData.difficulty}
                  onChange={(e) => updateField('difficulty', e.target.value)}
                >
                  {DIFFICULTY_OPTIONS.map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Estimated Duration *</label>
                <input
                  type="text"
                  className="form-input"
                  value={lessonData.duration}
                  onChange={(e) => updateField('duration', e.target.value)}
                  placeholder="e.g., 20 minutes"
                />
              </div>
            </div>

            <div className="form-group">
              <label>Curriculum Alignment *</label>
              <input
                type="text"
                className="form-input"
                value={lessonData.curriculum}
                onChange={(e) => updateField('curriculum', e.target.value)}
                placeholder="e.g., KS4 Computing - Advanced Networking"
              />
            </div>

            <div className="form-group">
              <label>Lesson Description *</label>
              <textarea
                className="form-textarea"
                value={lessonData.description}
                onChange={(e) => updateField('description', e.target.value)}
                placeholder="Brief description of what students will learn in this lesson..."
                rows={4}
              />
            </div>

            <div className="form-group">
              <label>Scenario (Optional)</label>
              <textarea
                className="form-textarea"
                value={lessonData.scenario}
                onChange={(e) => updateField('scenario', e.target.value)}
                placeholder="Optional: Provide a real-world scenario or context for the lesson (e.g., 'Mike complains he cannot access network files on his PC...')"
                rows={3}
              />
              <small style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', marginTop: '0.5rem', display: 'block' }}>
                💡 A scenario helps students understand the practical context and gives them a problem to solve
              </small>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="builder-step">
            <h3 className="step-title">📚 OCR Specification Topics</h3>
            <p className="step-description">Select all OCR GCSE Computer Science topics covered in this lesson</p>

            <div className="topics-container">
              {allTopics.map(topic => (
                <div
                  key={topic.code}
                  className={`topic-card ${lessonData.ocrTopics.includes(topic.code) ? 'selected' : ''}`}
                  onClick={() => toggleTopic(topic.code)}
                >
                  <div className="topic-header">
                    <div className="topic-checkbox">
                      {lessonData.ocrTopics.includes(topic.code) && <Check size={18} />}
                    </div>
                    <div className="topic-info">
                      <div className="topic-code-badge">{topic.code}</div>
                      <h4 className="topic-title">{topic.title}</h4>
                    </div>
                  </div>
                  
                  <div className="topic-category">{topic.category}</div>
                  
                  {topic.subtopics && topic.subtopics.length > 0 && (
                    <div className="topic-subtopics">
                      {topic.subtopics.slice(0, 3).map((sub, i) => (
                        <span key={i} className="subtopic-tag">{sub}</span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {lessonData.ocrTopics.length > 0 && (
              <div className="selected-topics-summary">
                <strong>{lessonData.ocrTopics.length} topic{lessonData.ocrTopics.length !== 1 ? 's' : ''} selected</strong>
              </div>
            )}
          </div>
        );

      case 3:
        return (
          <div className="builder-step">
            <h3 className="step-title">🎯 Learning Objectives</h3>
            <p className="step-description">What will students learn by completing this lesson?</p>

            <div className="objectives-list">
              {lessonData.objectives.map((obj, index) => (
                <div key={index} className="objective-item">
                  <div className="objective-number">{index + 1}</div>
                  <input
                    type="text"
                    className="form-input"
                    value={obj}
                    onChange={(e) => updateObjective(index, e.target.value)}
                    placeholder="e.g., Understand the role of a router in a network"
                  />
                  <button
                    className="btn-icon-danger"
                    onClick={() => removeObjective(index)}
                    disabled={lessonData.objectives.length === 1}
                    title="Remove objective"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
            </div>

            <button className="btn-add" onClick={addObjective}>
              <Plus size={18} />
              Add Learning Objective
            </button>
          </div>
        );

      case 4:
        return (
          <div className="builder-step">
            <h3 className="step-title">✅ Student Challenges</h3>
            <p className="step-description">Create tasks for students to complete during the lesson</p>

            {lessonData.challenges.length === 0 && (
              <div className="empty-state" style={{ marginBottom: '2rem' }}>
                <p>No challenges yet. Add challenges to guide students through the lesson.</p>
              </div>
            )}

            {lessonData.challenges.map((challenge, index) => (
              <div key={index} className="challenge-card">
                <div className="challenge-card-header">
                  <div className="challenge-number">Challenge {challenge.id}</div>
                  <button
                    className="btn-icon-small-danger"
                    onClick={() => removeChallenge(index)}
                    title="Remove challenge"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>

                <div className="form-group">
                  <label>Challenge Title *</label>
                  <input
                    type="text"
                    className="form-input"
                    value={challenge.title}
                    onChange={(e) => updateChallenge(index, 'title', e.target.value)}
                    placeholder="e.g., Test Basic Connectivity"
                  />
                </div>

                <div className="form-group">
                  <label>Description *</label>
                  <textarea
                    className="form-textarea"
                    value={challenge.description}
                    onChange={(e) => updateChallenge(index, 'description', e.target.value)}
                    placeholder="What should the student do?"
                    rows={3}
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Hint (optional)</label>
                    <input
                      type="text"
                      className="form-input"
                      value={challenge.hint}
                      onChange={(e) => updateChallenge(index, 'hint', e.target.value)}
                      placeholder="e.g., Select PC-1 and open the terminal"
                    />
                  </div>

                  <div className="form-group">
                    <label>Solution Command (optional)</label>
                    <input
                      type="text"
                      className="form-input"
                      value={challenge.solution}
                      onChange={(e) => updateChallenge(index, 'solution', e.target.value)}
                      placeholder="e.g., ping 192.168.1.20"
                    />
                  </div>
                </div>
              </div>
            ))}

            <button className="btn-add" onClick={addChallenge}>
              <Plus size={18} />
              Add Challenge
            </button>
          </div>
        );

      case 5:
        const selectedTopics = allTopics.filter(t => lessonData.ocrTopics.includes(t.code));
        
        return (
          <div className="builder-step">
            <h3 className="step-title">🎉 Review & Export</h3>
            <p className="step-description">Your lesson is ready! Review the details and export.</p>

            <div className="review-summary">
              <div className="summary-card">
                <h4>📋 Lesson Details</h4>
                <div className="summary-item">
                  <span className="label">Title:</span>
                  <span className="value">{lessonData.title || 'Not set'}</span>
                </div>
                <div className="summary-item">
                  <span className="label">Difficulty:</span>
                  <span className="value">{lessonData.difficulty}</span>
                </div>
                <div className="summary-item">
                  <span className="label">Duration:</span>
                  <span className="value">{lessonData.duration}</span>
                </div>
                <div className="summary-item">
                  <span className="label">Curriculum:</span>
                  <span className="value">{lessonData.curriculum}</span>
                </div>
              </div>

              <div className="summary-card">
                <h4>📚 OCR Topics ({lessonData.ocrTopics.length})</h4>
                <div className="topic-badges">
                  {selectedTopics.map(topic => (
                    <span key={topic.code} className="topic-badge">
                      {topic.code}: {topic.title}
                    </span>
                  ))}
                  {lessonData.ocrTopics.length === 0 && (
                    <span className="no-data">No topics selected</span>
                  )}
                </div>
              </div>

              <div className="summary-card">
                <h4>🖧 Network Topology</h4>
                <div className="summary-item">
                  <span className="label">Devices:</span>
                  <span className="value">{devices.length}</span>
                </div>
                <div className="summary-item">
                  <span className="label">Connections:</span>
                  <span className="value">{connections.length}</span>
                </div>
                <div className="network-devices-list">
                  {devices.map(dev => (
                    <div key={dev.id} className="device-preview">
                      {dev.name} ({dev.type.toUpperCase()}) - {dev.ip}
                    </div>
                  ))}
                </div>
              </div>

              <div className="summary-card">
                <h4>✅ Content</h4>
                <div className="summary-item">
                  <span className="label">Objectives:</span>
                  <span className="value">{lessonData.objectives.filter(o => o.trim()).length}</span>
                </div>
                <div className="summary-item">
                  <span className="label">Challenges:</span>
                  <span className="value">{lessonData.challenges.length}</span>
                </div>
              </div>
            </div>

            <div className="export-section">
              <h4>📥 Export Your Lesson</h4>
              <p>Download your lesson as a JavaScript file to add to lessons.js</p>
              
              <div className="code-preview-container">
                <div className="code-preview-header">Generated Lesson Code</div>
                <pre className="code-preview">{generateLessonCode()}</pre>
              </div>

              <button className="btn-download" onClick={downloadLesson}>
                <Download size={20} />
                Download .lesson File
              </button>

              <div className="instructions">
                <h5>How to use this lesson:</h5>
                <ol>
                  <li>Click "Download Lesson File" above</li>
                  <li>Open <code>src/data/lessons.js</code> in your project</li>
                  <li>Copy the downloaded lesson code</li>
                  <li>Add it to the <code>lessons</code> array</li>
                  <li>Save and refresh - your lesson will appear!</li>
                </ol>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  // Show warning if no network
  if (!hasNetwork) {
    return (
      <div className="lesson-builder">
        <div className="pro-notice">
          <Crown size={18} />
          <span>Lesson Builder - Pro Feature (Open Beta)</span>
        </div>

        <div className="empty-network-warning">
          <AlertCircle size={48} />
          <h3>No Network Designed Yet</h3>
          <p>Before creating a lesson, you need to design a network on the canvas.</p>
          
          <div className="instructions-list">
            <h4>How to create a lesson:</h4>
            <ol>
              <li>Close this panel</li>
              <li>Add devices from the left sidebar</li>
              <li>Connect them together</li>
              <li>Configure IPs and settings</li>
              <li>Test the network works</li>
              <li>Then come back here to create the lesson!</li>
            </ol>
          </div>

          <button 
            className="btn-primary"
            onClick={() => {
              const setPanelOpen = useStore.getState().setPanelOpen;
              setPanelOpen(false);
            }}
          >
            Close & Build Network
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="lesson-builder">
      {/* Pro Badge */}
      <div className="pro-notice">
        <Crown size={18} />
        <span>Lesson Builder - Pro Feature (Open Beta)</span>
      </div>

      {/* Progress Indicator */}
      <div className="builder-progress">
        {['Lesson Info', 'OCR Topics', 'Objectives', 'Challenges', 'Export'].map((label, index) => (
          <div
            key={index}
            className={`progress-step ${step > index + 1 ? 'completed' : ''} ${step === index + 1 ? 'active' : ''}`}
            onClick={() => setStep(index + 1)}
          >
            <div className="step-circle">
              {step > index + 1 ? <Check size={14} /> : index + 1}
            </div>
            <span className="step-label">{label}</span>
          </div>
        ))}
      </div>

      {/* Step Content */}
      <div className="builder-content">
        {renderStep()}
      </div>

      {/* Navigation */}
      <div className="builder-nav">
        <button
          className="btn-nav"
          onClick={() => setStep(step - 1)}
          disabled={step === 1}
        >
          <ChevronLeft size={18} />
          Previous
        </button>

        <div className="nav-spacer"></div>

        {step < 5 ? (
          <button
            className="btn-nav btn-primary"
            onClick={() => setStep(step + 1)}
          >
            Next
            <ChevronRight size={18} />
          </button>
        ) : (
          <button
            className="btn-nav btn-success"
            onClick={() => {
              downloadLesson();
              alert('Lesson downloaded! Add it to lessons.js to use it.');
            }}
          >
            <Download size={18} />
            Finish & Download
          </button>
        )}
      </div>
    </div>
  );
}
